import { useEffect, useState } from "react";
import { api, fmtUSD, fmtCliente } from "@/lib/api";
import { SectionCard } from "./SectionCard";

const MES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const etq = (p) => `${MES[Number(p.slice(5, 7)) - 1]} ${p.slice(2, 4)}`;

// Cobranzas acumuladas desde el primer informe cargado. Cada informe mensual suma; recargar un mes lo reemplaza.
export default function CobranzasAcumuladas({ vendedor, mes }) {
  const [d, setD] = useState(null);
  const [vista, setVista] = useState("vendedor");
  const [filtro, setFiltro] = useState("");

  useEffect(() => {
    let vivo = true;
    api.get("/report/cobranzas-acumuladas", { params: { vendedor, hasta: mes } })
      .then((r) => { if (vivo) setD(r.data); })
      .catch(() => { if (vivo) setD(null); });
    return () => { vivo = false; };
  }, [vendedor, mes]);

  if (!d || !(d.periodos || []).length) return null;
  const filas = vista === "vendedor" ? d.vendedores : d.clientes;
  const clave = vista === "vendedor" ? "vendedor" : "cliente";
  const lista = filas.filter((r) => !filtro || (r[clave] || "").toLowerCase().includes(filtro.toLowerCase()));
  const tab = (id, txt) => (
    <button onClick={() => setVista(id)}
      className={`text-xs px-3 py-1.5 rounded-full border ${vista === id ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200"}`}>{txt}</button>
  );

  return (
    <SectionCard
      id="cobranzas-acumuladas"
      testid="section-cobranzas-acumuladas"
      num="1A"
      title="Cobranzas acumuladas"
      description={`Suma de todos los informes cargados, de ${etq(d.desde)} a ${etq(d.hasta)}. Cada cobro cuenta para el vendedor que figura en él. Un mes que se vuelve a cargar reemplaza al anterior.`}
    >
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg bg-emerald-900 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-emerald-200">Acumulado</div>
          <div className="tm-num font-bold text-white text-lg" data-testid="cobranzas-acumulado-total">{fmtUSD(d.total)}</div>
        </div>
        <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-emerald-700">Informes acumulados</div>
          <div className="tm-num font-bold text-emerald-900 text-lg">{d.periodos.length}</div>
        </div>
        <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-emerald-700">Promedio mensual</div>
          <div className="tm-num font-bold text-emerald-900 text-lg">{fmtUSD(d.total / Math.max(1, Object.keys(d.por_mes).length))}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {tab("vendedor", "Acumulado vendedor")}
        {tab("cliente", "Acumulado cliente")}
        <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder={vista === "vendedor" ? "Buscar vendedor…" : "Buscar cliente…"}
          className="ml-auto border border-slate-200 rounded-md px-2 py-1 text-sm" />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 font-semibold text-xs">
              <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 z-[2]">{vista === "vendedor" ? "Vendedor" : "Cliente"}</th>
              {d.periodos.map((p) => <th key={p} className="text-right px-2 py-2 whitespace-nowrap">{etq(p)}</th>)}
              <th className="text-right px-3 py-2 whitespace-nowrap">Acumulado</th>
              <th className="text-right px-2 py-2">%</th>
              {vista === "cliente" && <th className="text-right px-3 py-2 whitespace-nowrap">Último cobro</th>}
            </tr>
          </thead>
          <tbody>
            {lista.map((r) => (
              <tr key={r[clave]} className="border-t border-slate-100 hover:bg-emerald-50/40">
                <td className="px-3 py-1.5 font-medium text-slate-800 sticky left-0 bg-white z-[1] whitespace-nowrap">
                  {vista === "cliente" ? fmtCliente(r.cliente) : r.vendedor}
                  <div className="text-[11px] text-slate-400 font-normal">{vista === "cliente" ? (r.vendedores || []).join(", ") : `${r.n_clientes} clientes`}</div>
                </td>
                {d.periodos.map((p) => <td key={p} className="px-2 text-right tm-num text-slate-600">{r.meses[p] ? fmtUSD(r.meses[p]) : "–"}</td>)}
                <td className="px-3 text-right tm-num font-semibold text-emerald-700">{fmtUSD(r.total)}</td>
                <td className="px-2 text-right text-xs text-slate-500">{r.pct}%</td>
                {vista === "cliente" && <td className="px-3 text-right text-xs text-slate-500">{r.ultimo_cobro}</td>}
              </tr>
            ))}
            <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
              <td className="px-3 py-2 sticky left-0 bg-slate-50">TOTAL</td>
              {d.periodos.map((p) => <td key={p} className="px-2 text-right tm-num">{d.por_mes[p] ? fmtUSD(d.por_mes[p]) : "–"}</td>)}
              <td className="px-3 text-right tm-num text-emerald-800">{fmtUSD(d.total)}</td>
              <td className="px-2 text-right text-xs">100%</td>
              {vista === "cliente" && <td />}
            </tr>
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}
